import { KCSDK, KeyboardEventData, KeyboardEventType } from "@/sdk"
import {
  NewAgentKeyCodeMap,
  NewAgentNumLockKeyCodeMap,
  OldAgentKeyCodeMap,
  OldAgentNumLockKeyCodeMap,
} from "@/sdk/constants"
import { useAppSelector } from "@/store"
import { selectIsSafariBrowser } from "@/stores/app_slice"
import { selectIsUseMacCommonShortcut } from "@/stores/remote_desk_state_slice"
import { ClipboardUsecase } from "@/usecases/clipboard"
import { OS, getOS } from "@/utils/os"
import { RefObject, useEffect, useRef } from "react"
import { ConnectionUseCase } from "./connection"

// 用于上报键盘事件
export const useReportKeyboard = (
  onVideoElementClickAndKeyDown: (
    videoElement: HTMLVideoElement | null
  ) => void,
  videoRef: RefObject<HTMLVideoElement>
): void => {
  const processor = useRef<KeyboardProcessor>(new OldAgentKeyboardProcessor())
  const isUseMacCommonShortcutRef = useRef(false)
  const isSafariBrowserRef = useRef(false)
  const isGameModelRef = useRef(false)
  // Mac 系统是否使用本机系统的复制粘贴快捷键
  const isUseMacCommonShortcut = useAppSelector(selectIsUseMacCommonShortcut)
  const isSafariBrowser = useAppSelector(selectIsSafariBrowser)
  const isGameModel = useAppSelector(
    (state) => state.remoteDeskState.isGameModel
  )

  useEffect(() => {
    isUseMacCommonShortcutRef.current = isUseMacCommonShortcut
    isSafariBrowserRef.current = isSafariBrowser
    isGameModelRef.current = isGameModel

    // 设置快捷键映射
    processor.current.updateConfig({
      isUseMacCopyPaste: isUseMacCommonShortcut,
      isSafariBrowser,
      isGameModel,
    })
  }, [isGameModel, isSafariBrowser, isUseMacCommonShortcut])

  useEffect(() => {
    // 拦截键盘事件
    processor.current.interceptKeyEvents(
      onVideoElementClickAndKeyDown,
      videoRef
    )
    ConnectionUseCase.setAgentVersionListener((isNewAgent) => {
      if (isNewAgent) {
        processor.current = new NewAgentKeyboardProcessor()
        processor.current.updateConfig({
          isUseMacCopyPaste: isUseMacCommonShortcutRef.current,
          isSafariBrowser: isSafariBrowserRef.current,
          isGameModel: isGameModelRef.current,
        })
      }

      // 处理键盘事件
      processor.current.interceptKeyEvents(
        onVideoElementClickAndKeyDown,
        videoRef
      )
    })

    const dispose = (): void => {
      // 退出页面时取消拦截键盘事件
      processor.current.recoverKeyEvents()
    }
    return dispose
  }, [])
}

interface KeyboardConfig {
  // 是否使用 Mac 的复制粘贴快捷键
  isUseMacCopyPaste: boolean
  // 是否是 Safari 浏览器
  isSafariBrowser: boolean
  // 是否开启游戏模式
  isGameModel: boolean
}

const MODIFIER_KEY_CODES = new Set([
  "Control",
  "ControlLeft",
  "ControlRight",
  "Shift",
  "ShiftLeft",
  "ShiftRight",
  "Alt",
  "AltLeft",
  "AltRight",
  "Meta",
  "MetaLeft",
  "MetaRight",
])

const SYNTHETIC_REPEAT_MODIFIER_KEY_CODES = new Set([
  "Control",
  "ControlLeft",
  "ControlRight",
  "Shift",
  "ShiftLeft",
  "ShiftRight",
  "Alt",
  "AltLeft",
  "AltRight",
])

const MODIFIER_REPEAT_IDLE_MS = 200

abstract class KeyboardProcessor {
  readonly pressedKeys = new Set<string>()
  // 追踪正在进行快捷键映射的功能键（用于Mac快捷键映射）
  readonly shortcutMappingKeys = new Set<string>()
  readonly browserClipboardShortcutModes = new Map<
    string,
    "allow" | "prevent"
  >()

  protected readonly intercepters: KeyboardInterceptor[]
  // 是否拦截了 Meta 键
  isInterceptMeta = false

  // 存储回调和引用，避免层层传递
  private onVideoElementClickAndKeyDown:
    | ((videoElement: HTMLVideoElement | null) => void)
    | null = null

  private videoRef: RefObject<HTMLVideoElement> | null = null

  constructor() {
    this.intercepters = [
      // 在没有真实 repeat 时，为按下中的修饰键补发 repeat
      new ModifierRepeatKeyboardInterceptor(this),
      // 处理 Meta 键按下
      new PressMetaInterceptor(this),
      // Windows 系统的一些兼容性处理
      new WindowsCompatibleKeyboardInterceptor(this),
      // mac 系统的一些兼容性处理
      new MacCompatibleKeyboardInterceptor(this),
      // 处理 Mac 快捷键映射
      new MacShortcutKeyboardInterceptor(this),
      // 恢复 Meta 事件
      new RecoverMetaInterceptor(this),
    ]
  }

  config: KeyboardConfig = {
    isUseMacCopyPaste: false,
    isSafariBrowser: false,
    isGameModel: false,
  }

  readonly os = getOS()

  updateConfig(config: Partial<KeyboardConfig>) {
    this.config = { ...this.config, ...config }
  }

  /// 拦截键盘事件
  interceptKeyEvents(
    onVideoElementClickAndKeyDown: (
      videoElement: HTMLVideoElement | null
    ) => void,
    videoRef: RefObject<HTMLVideoElement>
  ): void {
    // 存储回调和引用
    this.onVideoElementClickAndKeyDown = onVideoElementClickAndKeyDown
    this.videoRef = videoRef

    window.onkeydown = (event) => this.onKeyDown(event)
    window.onkeyup = (event) => this.onKeyUp(event)
    window.addEventListener("focus", () => this.onFocus())
    window.addEventListener("blur", () => this.onBlur())
  }

  /// 恢复键盘事件
  recoverKeyEvents(): void {
    window.onkeydown = null
    window.onkeyup = null
    window.onfocus = null
    window.onblur = null
    this.intercepters.forEach((interceptor) => interceptor.reset())
    this.pressedKeys.clear()
  }

  private onKeyDown(event: KeyboardEvent): void {
    // 游戏模式下，按下键盘任意键（除了esc和u），锁定鼠标
    const noLockMouseKeyList = new Set(["Escape", "KeyU"])
    if (!noLockMouseKeyList.has(event.code)) {
      this.onVideoElementClickAndKeyDown?.(this.videoRef?.current ?? null)
    }

    const shortcutMode = this.handleSafariBrowserClipboardShortcut(event)
    if (shortcutMode !== null) {
      if (shortcutMode === "prevent") {
        event.preventDefault()
      }
      return
    }

    this.processKeyboardData(event)
    if (!this.shouldAllowBrowserClipboardShortcut(event)) {
      event.preventDefault()
    }
  }

  private onKeyUp(event: KeyboardEvent): void {
    const shortcutMode = this.consumeSafariBrowserClipboardShortcut(event)
    if (shortcutMode !== null) {
      if (shortcutMode === "prevent") {
        event.preventDefault()
      }
      return
    }

    this.processKeyboardData(event)
    if (!this.shouldAllowBrowserClipboardShortcut(event)) {
      event.preventDefault()
    }
  }

  protected onFocus() {}

  protected onBlur() {
    // 重置 Meta 拦截标志，避免切换应用回来后误触发快捷键
    this.isInterceptMeta = false
    this.browserClipboardShortcutModes.clear()
    this.intercepters.forEach((interceptor) => interceptor.reset())
    this.releaseKeys()
  }

  // 释放当前按下的按键
  releaseKeys() {
    this.pressedKeys.forEach((key) => {
      const event = new KeyboardEvent("keyup", { code: key })
      this.sendData(event)
    })
    this.pressedKeys.clear()
  }

  // 处理键盘事件
  protected abstract processKeyboardData(event: KeyboardEvent): void
  abstract sendData(event: KeyboardEvent): void

  hasPress(code: string): boolean {
    return this.pressedKeys.has(code)
  }

  deletePressedKey(code: string) {
    this.pressedKeys.delete(code)
  }

  get pressedKeyNum(): number {
    return this.pressedKeys.size
  }

  getPressedModifierKeys(): string[] {
    return [...this.pressedKeys].filter((code) => MODIFIER_KEY_CODES.has(code))
  }

  getPressedSyntheticRepeatModifierKeys(): string[] {
    return [...this.pressedKeys].filter((code) =>
      SYNTHETIC_REPEAT_MODIFIER_KEY_CODES.has(code)
    )
  }

  getPressedNonModifierKeys(): string[] {
    return [...this.pressedKeys].filter((code) => !MODIFIER_KEY_CODES.has(code))
  }

  shouldUseRepeatKeyboardProtocol(): boolean {
    return false
  }

  private handleSafariBrowserClipboardShortcut(
    event: KeyboardEvent
  ): "allow" | "prevent" | null {
    if (
      this.os !== OS.MacOS ||
      !this.config.isSafariBrowser ||
      !this.config.isUseMacCopyPaste ||
      event.type !== "keydown" ||
      (event.code !== "KeyC" && event.code !== "KeyV")
    ) {
      return null
    }

    const isMetaCopy = event.metaKey && event.code === "KeyC"
    const isMetaPaste = event.metaKey && event.code === "KeyV"
    const isCtrlPaste = event.ctrlKey && event.code === "KeyV"

    if (!isMetaCopy && !isMetaPaste && !isCtrlPaste) {
      return null
    }

    let shortcutMode: "allow" | "prevent" = "prevent"
    this.browserClipboardShortcutModes.set(event.code, shortcutMode)
    this.pressedKeys.forEach((key) => {
      if (key.startsWith("Meta")) {
        this.browserClipboardShortcutModes.set(key, shortcutMode)
      }
    })
    this.pressedKeys.forEach((key) => {
      if (key.startsWith("Meta")) {
        this.pressedKeys.delete(key)
      }
    })
    this.isInterceptMeta = false
    this.shortcutMappingKeys.clear()

    if (isMetaCopy) {
      ClipboardUsecase.prepareSafariClipboardWriteFromRemote()
      this.sendRemoteClipboardShortcut("KeyC")
    } else if (isMetaPaste) {
      if (ClipboardUsecase.shouldUseRemoteClipboardForSafariPaste()) {
        this.sendRemoteClipboardShortcut("KeyV")
      } else {
        shortcutMode = "allow"
        this.browserClipboardShortcutModes.set(event.code, shortcutMode)
        this.pressedKeys.forEach((key) => {
          if (key.startsWith("Meta")) {
            this.browserClipboardShortcutModes.set(key, shortcutMode)
          }
        })
        ClipboardUsecase.handleSafariPasteShortcutToRemote().then((success) => {
          if (success) {
            this.sendRemoteClipboardShortcut("KeyV")
          }
        })
      }
    } else if (isCtrlPaste) {
      if (ClipboardUsecase.shouldUseRemoteClipboardForSafariPaste()) {
        this.sendRemoteClipboardShortcut("KeyV")
      } else if (
        ClipboardUsecase.shouldUseCachedLocalClipboardForSafariPaste()
      ) {
        ClipboardUsecase.syncCachedLocalClipboardTextToRemote()
        this.sendRemoteClipboardShortcut("KeyV")
      } else {
        ClipboardUsecase.syncLocalClipboardTextToRemoteByGesture()
          .catch((error) => {
            console.warn("Safari clipboard read by Ctrl+V error: ", error)
          })
          .finally(() => {
            this.sendRemoteClipboardShortcut("KeyV")
          })
      }
    }

    return shortcutMode
  }

  private consumeSafariBrowserClipboardShortcut(
    event: KeyboardEvent
  ): "allow" | "prevent" | null {
    const shortcutMode = this.browserClipboardShortcutModes.get(event.code)
    if (!shortcutMode) {
      return null
    }

    this.browserClipboardShortcutModes.delete(event.code)
    return shortcutMode
  }

  private sendRemoteClipboardShortcut(code: "KeyC" | "KeyV") {
    this.sendData(new KeyboardEvent("keydown", { code: "Control" }))
    this.sendData(
      new KeyboardEvent("keydown", {
        code,
        ctrlKey: true,
        metaKey: false,
      })
    )
    this.sendData(
      new KeyboardEvent("keyup", {
        code,
        ctrlKey: true,
        metaKey: false,
      })
    )
    this.sendData(new KeyboardEvent("keyup", { code: "Control" }))
  }

  private shouldAllowBrowserClipboardShortcut(event: KeyboardEvent) {
    if (
      this.os !== OS.MacOS ||
      !this.config.isSafariBrowser ||
      !this.config.isUseMacCopyPaste
    ) {
      return false
    }

    const isShortcutKey = event.code === "KeyC" || event.code === "KeyV"
    const isMetaKey = event.code.startsWith("Meta")
    const isClipboardCombo = event.metaKey && isShortcutKey
    const isClipboardRelease =
      isShortcutKey &&
      this.shortcutMappingKeys.has(event.code) &&
      event.type === "keyup"
    const isMetaReleaseForClipboard =
      isMetaKey && this.shortcutMappingKeys.size > 0 && event.type === "keyup"

    return isClipboardCombo || isClipboardRelease || isMetaReleaseForClipboard
  }
}

abstract class KeyboardInterceptor {
  protected readonly processor: KeyboardProcessor

  constructor(processor: KeyboardProcessor) {
    this.processor = processor
  }

  abstract shouldIntercept(event: KeyboardEvent): boolean

  reset(): void {}
}

class ModifierRepeatKeyboardInterceptor extends KeyboardInterceptor {
  private repeatTimer: ReturnType<typeof setTimeout> | null = null
  private nativeRepeatCode: string | null = null
  private lastNativeRepeatAt = 0

  shouldIntercept(event: KeyboardEvent): boolean {
    if (!this.processor.shouldUseRepeatKeyboardProtocol()) {
      return false
    }

    if (event.type === "keydown" && event.repeat) {
      this.nativeRepeatCode = event.code
      this.lastNativeRepeatAt = Date.now()
    } else if (event.type === "keyup" && event.code === this.nativeRepeatCode) {
      this.nativeRepeatCode = null
    }

    this.refreshRepeatTimer()
    return false
  }

  override reset(): void {
    this.clearRepeatTimer()
    this.nativeRepeatCode = null
    this.lastNativeRepeatAt = 0
  }

  private refreshRepeatTimer() {
    this.clearRepeatTimer()

    if (this.shouldSuspendSyntheticRepeat()) {
      return
    }

    if (this.processor.getPressedSyntheticRepeatModifierKeys().length === 0) {
      this.nativeRepeatCode = null
      this.lastNativeRepeatAt = 0
      return
    }

    this.repeatTimer = setTimeout(() => {
      if (this.shouldSuspendSyntheticRepeat()) {
        this.refreshRepeatTimer()
        return
      }

      if (this.hasRecentNativeRepeat()) {
        this.refreshRepeatTimer()
        return
      }

      const modifierKeys =
        this.processor.getPressedSyntheticRepeatModifierKeys()
      if (modifierKeys.length === 0) {
        this.nativeRepeatCode = null
        this.lastNativeRepeatAt = 0
        this.repeatTimer = null
        return
      }

      const nextRepeatCode =
        modifierKeys[Math.floor(Math.random() * modifierKeys.length)]

      this.processor.sendData(this.createRepeatKeyboardEvent(nextRepeatCode))
      this.refreshRepeatTimer()
    }, this.getNextCheckDelay())
  }

  private clearRepeatTimer() {
    if (this.repeatTimer) {
      clearTimeout(this.repeatTimer)
      this.repeatTimer = null
    }
  }

  private createRepeatKeyboardEvent(code: string): KeyboardEvent {
    const pressedModifierKeys = new Set(this.processor.getPressedModifierKeys())

    return new KeyboardEvent("keydown", {
      code,
      repeat: true,
      ctrlKey: this.hasPressedModifierKey(pressedModifierKeys, "Control"),
      shiftKey: this.hasPressedModifierKey(pressedModifierKeys, "Shift"),
      altKey: this.hasPressedModifierKey(pressedModifierKeys, "Alt"),
      metaKey: this.hasPressedModifierKey(pressedModifierKeys, "Meta"),
    })
  }

  private hasPressedModifierKey(
    pressedModifierKeys: Set<string>,
    prefix: "Control" | "Shift" | "Alt" | "Meta"
  ): boolean {
    for (const code of pressedModifierKeys) {
      if (code.startsWith(prefix)) {
        return true
      }
    }

    return false
  }

  private hasRecentNativeRepeat(): boolean {
    return (
      this.lastNativeRepeatAt > 0 &&
      Date.now() - this.lastNativeRepeatAt < MODIFIER_REPEAT_IDLE_MS
    )
  }

  private getNextCheckDelay(): number {
    if (!this.hasRecentNativeRepeat()) {
      return MODIFIER_REPEAT_IDLE_MS
    }

    return Math.max(
      MODIFIER_REPEAT_IDLE_MS - (Date.now() - this.lastNativeRepeatAt),
      0
    )
  }

  private shouldSuspendSyntheticRepeat(): boolean {
    return (
      this.processor.isInterceptMeta ||
      this.processor.shortcutMappingKeys.size > 0
    )
  }
}

// 针对 Windows 系统的一些兼容性处理
class WindowsCompatibleKeyboardInterceptor extends KeyboardInterceptor {
  shouldIntercept(event: KeyboardEvent): boolean {
    if (this.processor.os !== OS.Windows) {
      return false
    }

    if (event.type === "keyup") {
      // Windows 中 当同时按下 Shift Left，Shift Right，只会触发一次释放（已左右释放的 shift 键为准）
      // 如果记录中存在其他本按下的Shift，则需要手动释放
      if (this.processor.os === OS.Windows) {
        let remainShift: string | null = null
        if (event.code === "ShiftLeft") {
          remainShift = "ShiftRight"
        } else if (event.code === "ShiftRight") {
          remainShift = "ShiftLeft"
        }
        if (remainShift && this.processor.hasPress(remainShift)) {
          this.processor.deletePressedKey(remainShift)
          this.processor.sendData(
            new KeyboardEvent("keyup", { code: remainShift })
          )
        }
      }
    }
    return false
  }
}

// 处理 Mac 快捷键映射
class MacShortcutKeyboardInterceptor extends KeyboardInterceptor {
  // 记录当前正在处理的快捷键的功能键（如V、C、X等）
  private interceptedKey: string | null = null
  // 记录是否已经发送了Control键按下事件（用于在Cmd抬起时发送Control抬起）
  private hasControlSent = false
  // 用于检测功能键是否抬起的定时器（Mac系统中Cmd按着时不会触发keyup）
  readonly keyupTimers = new Map<string, ReturnType<typeof setTimeout>>()

  shouldIntercept(event: KeyboardEvent): boolean {
    if (
      this.processor.os !== OS.MacOS ||
      !this.processor.config.isUseMacCopyPaste
    ) {
      // 非 Mac 系统，或未开启快捷键映射，不做拦截
      return false
    }

    // 需要处理的快捷键列表
    const keyList = new Set([
      "KeyC",
      "KeyX",
      "KeyV",
      "KeyZ",
      "KeyA",
      "KeyS",
      "KeyO",
      "KeyN",
      "KeyP",
      "KeyF",
      "KeyH",
      "KeyW",
      "Tab",
    ])

    if (
      this.processor.isInterceptMeta &&
      event.type === "keydown" &&
      keyList.has(event.code)
    ) {
      // 快捷键按下
      this.processShortcutDown(event)
      return true
    }

    // 拦截所有功能键的抬起事件（当Control已经发送时）
    // 注意：不能只拦截interceptedKey，因为用户可能连续按多个键（如先按A再按C）
    if (
      this.hasControlSent &&
      event.type === "keyup" &&
      keyList.has(event.code)
    ) {
      // 功能键抬起
      this.processShortcutUp(event)
      return true
    }

    // 处理Cmd键抬起（如果之前发送过Control键按下）
    if (
      this.hasControlSent &&
      event.type === "keyup" &&
      event.code.startsWith("Meta")
    ) {
      this.processMetaUp(event)
      return true
    }

    // 关键修复：如果已经发送了Control（Cmd还在按下），继续拦截快捷键列表中的按键
    if (
      this.hasControlSent &&
      event.type === "keydown" &&
      keyList.has(event.code)
    ) {
      // 如果这个键已经在shortcutMappingKeys中，说明是长按repeat事件
      if (this.processor.shortcutMappingKeys.has(event.code)) {
        // 重置定时器，因为用户还在按着这个键
        this.resetKeyupTimer(event.code, event)
        // 发送keydown事件，支持长按连续执行
        const keydownEvent = new KeyboardEvent("keydown", {
          code: event.code,
          key: event.key,
          ctrlKey: true,
          metaKey: false,
          repeat: event.repeat,
        })
        this.processor.sendData(keydownEvent)
        return true
      }
      this.processShortcutDown(event)
      return true
    }

    return false
  }

  // 处理快捷键按下
  private processShortcutDown(event: KeyboardEvent) {
    // 记录当前拦截的功能键
    this.interceptedKey = event.code
    // 手动添加到pressedKeys，确保后续的keyup事件能被处理
    this.processor.pressedKeys.add(event.code)
    // 添加到shortcutMappingKeys，用于Mac兼容性处理
    this.processor.shortcutMappingKeys.add(event.code)
    // 取消上层拦截 Meta 键事件
    this.processor.isInterceptMeta = false

    // 只在第一次发送Control按下，避免重复按键时重复发送
    if (!this.hasControlSent) {
      this.processor.sendData(new KeyboardEvent("keydown", { code: "Control" }))
      this.hasControlSent = true
    }

    // 发送功能键按下
    const keydownEvent = new KeyboardEvent("keydown", {
      code: event.code,
      key: event.key,
      ctrlKey: true,
      altKey: event.altKey,
      shiftKey: event.shiftKey,
      metaKey: false,
    })
    this.processor.sendData(keydownEvent)

    // 关键修复：在Mac系统中，当Cmd被按着时，功能键的keyup不会被触发
    // 使用定时器检测：如果100ms内没有收到repeat事件，说明用户已经抬起了功能键
    this.resetKeyupTimer(event.code, event)
  }

  // 重置/启动keyup检测定时器
  private resetKeyupTimer(code: string, event: KeyboardEvent) {
    // 清除之前的定时器
    const existingTimer = this.keyupTimers.get(code)
    if (existingTimer) {
      clearTimeout(existingTimer)
    }

    // 启动新的定时器，100ms后如果没有收到repeat事件，发送keyup
    const timer = setTimeout(() => {
      if (this.processor.shortcutMappingKeys.has(code)) {
        const keyupEvent = new KeyboardEvent("keyup", {
          code,
          key: event.key,
          ctrlKey: true,
          metaKey: false,
        })
        this.processor.sendData(keyupEvent)
        this.processor.shortcutMappingKeys.delete(code)
        this.processor.pressedKeys.delete(code)
      }
      this.keyupTimers.delete(code)
    }, 10)

    this.keyupTimers.set(code, timer)
  }

  // 清除所有keyup定时器
  private clearAllKeyupTimers() {
    this.keyupTimers.forEach((timer) => clearTimeout(timer))
    this.keyupTimers.clear()
  }

  // 处理功能键抬起（V、C、X等）
  private processShortcutUp(event: KeyboardEvent) {
    // 清除拦截标记
    this.interceptedKey = null
    // 手动从pressedKeys中删除
    this.processor.pressedKeys.delete(event.code)
    // 从shortcutMappingKeys中删除
    this.processor.shortcutMappingKeys.delete(event.code)

    // 发送功能键抬起，清除metaKey状态，但保持ctrlKey=true（因为Control还在按下）
    const newEvent = new KeyboardEvent("keyup", {
      code: event.code,
      key: event.key,
      ctrlKey: true, // Control还在按下状态
      altKey: event.altKey,
      shiftKey: event.shiftKey,
      metaKey: false, // 清除Meta标志
    })
    this.processor.sendData(newEvent)
  }

  // 处理Cmd键抬起
  private processMetaUp(event: KeyboardEvent) {
    // 清除所有keyup定时器
    this.clearAllKeyupTimers()

    // 在Mac系统中，当Cmd键抬起时，其他键的keyup可能不会被触发
    // 所以需要手动释放所有正在映射的功能键
    this.processor.shortcutMappingKeys.forEach((key) => {
      const keyupEvent = new KeyboardEvent("keyup", {
        code: key,
        ctrlKey: true, // Control还在按下
        metaKey: false,
      })
      this.processor.sendData(keyupEvent)
      this.processor.pressedKeys.delete(key)
    })
    this.processor.shortcutMappingKeys.clear()

    // 发送Control键抬起
    this.processor.sendData(new KeyboardEvent("keyup", { code: "Control" }))
    this.hasControlSent = false
    // 手动从pressedKeys中删除Meta键
    this.processor.pressedKeys.delete(event.code)
  }
}

// 拦截 Meta 键按下事件（仅在 Mac 系统上）
class PressMetaInterceptor extends KeyboardInterceptor {
  shouldIntercept(event: KeyboardEvent): boolean {
    // 只在 Mac 系统上拦截 Meta 键，用于快捷键映射
    if (this.processor.os !== OS.MacOS) {
      return false
    }

    if (event.code.startsWith("Meta") && event.type === "keydown") {
      this.processor.isInterceptMeta = true
      // 手动添加到pressedKeys，确保后续的keyup事件能被处理
      this.processor.pressedKeys.add(event.code)

      return true
    }

    return false
  }
}

// 恢复拦截的 Meta 事件
class RecoverMetaInterceptor extends KeyboardInterceptor {
  shouldIntercept(event: KeyboardEvent): boolean {
    // 是否正在拦截 Meta 键
    const isIntercept = this.processor.isInterceptMeta
    // 当前事件是否为 Meta 键
    const isMeta = event.code.startsWith("Meta")
    const isKeyDown = event.type === "keydown"
    const isKeyUp = event.type === "keyup"
    // 当前事件是否为 Meta 键抬起事件
    const isMetaUp = isMeta && isKeyUp
    // 当前事件是否为非 Meta 键按下
    const isOtherKeyDown = !isMeta && isKeyDown

    if (isIntercept && (isMetaUp || isOtherKeyDown)) {
      // 正在拦截 Meta 键，且当前事件为 Meta 键抬起，或者非 Meta 键按下，需要补发 Meta 键按下事件
      const recoverCode = isMetaUp ? event.code : "Meta"
      this.processor.sendData(
        new KeyboardEvent("keydown", { code: recoverCode })
      )
      // 发送当前事件
      this.processor.sendData(event)
      // 取消 Meta 键拦截
      this.processor.isInterceptMeta = false

      return true
    }

    return false
  }
}

// 针对 Mac OS 的一些兼容性处理
// eslint-disable-next-line @typescript-eslint/no-unused-vars
class MacCompatibleKeyboardInterceptor extends KeyboardInterceptor {
  shouldIntercept(event: KeyboardEvent): boolean {
    if (this.processor.os !== OS.MacOS) {
      return false
    }

    if (event.type === "keyup") {
      // MacOS 中当 Cmd 键释放，其余的按键释放不会被检测到，所以需要手动释放
      if (this.processor.os === OS.MacOS) {
        const isMetaKey = event.code.startsWith("Meta")
        if (isMetaKey) {
          // 如果正在进行快捷键映射（shortcutMappingKeys不为空），跳过releaseKeys
          // 让MacShortcutKeyboardInterceptor来处理这些键的释放
          if (this.processor.shortcutMappingKeys.size === 0) {
            this.processor.releaseKeys()
          }
        }
      }
    }
    return false
  }
}
class OldAgentKeyboardProcessor extends KeyboardProcessor {
  private isCapsLock = false
  readonly osInfo: number

  constructor() {
    super()
    this.osInfo = 0x0
    switch (this.os) {
      case OS.MacOS: {
        this.osInfo = 0x1
        break
      }
      case OS.Windows: {
        this.osInfo = 0x2
        break
      }
      case OS.Linux: {
        this.osInfo = 0x4
        break
      }
    }
    this.osInfo <<= 16
  }

  processKeyboardData(event: KeyboardEvent): void {
    for (const interceptor of this.intercepters) {
      if (interceptor.shouldIntercept(event)) {
        // 拦截键盘事件
        return
      }
    }
    // 发送事件
    this.sendData(event)
  }

  // 发送键盘事件
  sendData(event: KeyboardEvent) {
    const type =
      event.type === "keydown"
        ? KeyboardEventType.keydown
        : KeyboardEventType.keyup

    let code = OldAgentKeyCodeMap.get(event.code)
    const isNumLock = event.getModifierState("NumLock")
    const isMacOS = this.os === OS.MacOS
    if ((isMacOS || isNumLock) && OldAgentNumLockKeyCodeMap.has(event.code)) {
      code = OldAgentNumLockKeyCodeMap.get(event.code)
    }

    if (code === undefined) {
      console.warn(`Unknown key code: ${event.code}`)
      return
    }

    if (event.getModifierState("Delete") && event.altKey && event.ctrlKey) {
      code = 165
    }

    if (event.code === "MetaLeft") {
      code = OldAgentKeyCodeMap.get("WinLeft")
    } else if (event.code === "MetaRight") {
      code = OldAgentKeyCodeMap.get("WinRight")
    }

    // 同步 CapsLock 状态
    this.syncCapsLockEvent(event)

    if (event.code === "CapsLock") {
      // 如果按键是 CapsLock，不再发送
      return
    }

    let state = 0
    if (this.isCapsLock) {
      state |= 0x8
    }
    if (event.ctrlKey) {
      state |= 0x1
    }
    if (event.altKey) {
      state |= 0x4
    }
    if (event.metaKey) {
      state |= 0x10
    }
    if (event.shiftKey) {
      state |= 0x2
    }
    if (event.code === "ShiftRight") {
      state |= 0x8
    }

    state |= this.osInfo

    const keyboardData: KeyboardEventData = {
      event: type,
      code: code!,
      state,
    }

    // console.log("[keyboard] send old", {
    //   type: event.type,
    //   code: event.code,
    //   key: event.key,
    //   // ctrlKey: event.ctrlKey,
    //   // altKey: event.altKey,
    //   // shiftKey: event.shiftKey,
    //   // metaKey: event.metaKey,

    //   // keyboardData,
    // })

    // 发送键盘事件
    KCSDK.sendKeyboardData(keyboardData)
  }

  private syncCapsLockEvent(event: KeyboardEvent) {
    const isMacOS = this.os === OS.MacOS
    const curCapsLockStatus = event.getModifierState("CapsLock")
    if (curCapsLockStatus !== this.isCapsLock) {
      const capsLockCode = OldAgentKeyCodeMap.get("CapsLock")!
      const releaseState = this.osInfo << 16
      const pressState = 0x8 | releaseState
      if (isMacOS) {
        // 本机 CapsLock 状态与远端不一致
        if (curCapsLockStatus) {
          // 补发一个 CapsLock down 事件
          const keyboardData: KeyboardEventData = {
            event: KeyboardEventType.keydown,
            code: capsLockCode,
            state: pressState,
          }
          KCSDK.sendKeyboardData(keyboardData)
        } else {
          const keyboardData: KeyboardEventData = {
            event: KeyboardEventType.keyup,
            code: capsLockCode,
            state: releaseState,
          }
          KCSDK.sendKeyboardData(keyboardData)
        }
      } else if (this.os === OS.Windows) {
        const state = curCapsLockStatus ? pressState : releaseState
        let keyboardData: KeyboardEventData = {
          event: KeyboardEventType.keydown,
          code: capsLockCode,
          state,
        }
        KCSDK.sendKeyboardData(keyboardData)
        keyboardData = {
          event: KeyboardEventType.keyup,
          code: capsLockCode,
          state,
        }
        KCSDK.sendKeyboardData(keyboardData)
      }
      this.isCapsLock = curCapsLockStatus
    }
  }
}

class NewAgentKeyboardProcessor extends KeyboardProcessor {
  override shouldUseRepeatKeyboardProtocol(): boolean {
    return this.useRepeatKeyboardProtocol()
  }

  override processKeyboardData(event: KeyboardEvent) {
    // console.log(
    //   // event,
    //   "type:",
    //   event.type,
    //   "repeat:",
    //   event.repeat,
    //   "keyCode:",
    //   event.keyCode,
    //   "key:",
    //   event.key,
    //   "code:",
    //   event.code,
    //   "bubbles:",
    //   event.bubbles
    // )

    const isRepeatKeyDown =
      event.type === "keydown" && this.pressedKeys.has(event.code)
    const useRepeatProtocol = this.useRepeatKeyboardProtocol()

    // 记录按下的键 (除了 NumLock 和 CapsLock)
    if (event.type === "keydown") {
      if (isRepeatKeyDown) {
        if (!useRepeatProtocol || !event.repeat) {
          return
        }
      }

      if (!isRepeatKeyDown && !this.isToggleKey(event.code)) {
        this.pressedKeys.add(event.code)
      }
    } else {
      if (!this.pressedKeys.has(event.code)) {
        // 没有记录按下的键，不处理
        return
      }
      this.pressedKeys.delete(event.code)
    }

    for (const interceptor of this.intercepters) {
      if (interceptor.shouldIntercept(event)) {
        // 拦截键盘事件
        return
      }
    }

    // 发送事件
    this.sendData(event)

    if (this.pressedKeys.size === 0) {
      KCSDK.sendCleanKeyboardData()
    }
  }

  // 发送键盘事件
  override sendData(event: KeyboardEvent) {
    if (!ConnectionUseCase.isPeerConnected()) {
      return
    }

    // const useRepeatProtocol = this.useRepeatKeyboardProtocol()
    const type = this.getKeyboardEventType(event)

    const isNumLock = event.getModifierState("NumLock")

    // console.log("event.code:", event.code)
    let keyCode = NewAgentKeyCodeMap.get(event.code)
    const isMacOS = this.os === OS.MacOS

    if ((isMacOS || isNumLock) && NewAgentNumLockKeyCodeMap.has(event.code)) {
      keyCode = NewAgentNumLockKeyCodeMap.get(event.code)
    }

    if (keyCode === undefined) {
      console.warn(`Unknown key code: ${event.code}`)
      return
    }

    const state = this.getKeyboardState(event)

    const keyboardData: KeyboardEventData = {
      event: type,
      code: keyCode,
      state,
    }

    // console.log(
    //   "keyboardData 方案:",
    //   useRepeatProtocol ? "new" : "old",
    //   // type: event.type,
    //   // code: event.code,
    //   // key: event.key,
    //   // ctrlKey: event.ctrlKey,
    //   // altKey: event.altKey,
    //   // shiftKey: event.shiftKey,
    //   // metaKey: event.metaKey,
    //   "，event:",
    //   keyboardData.event,
    //   "，code:",
    //   keyboardData.code,
    //   "，state:",
    //   keyboardData.state
    // )

    KCSDK.sendKeyboardData(keyboardData)
  }

  private isToggleKey(code: string): boolean {
    return code === "NumLock" || code === "CapsLock"
  }

  private getKeyboardEventType(event: KeyboardEvent): KeyboardEventType {
    if (event.type === "keyup") {
      return KeyboardEventType.keyup
    }

    if (event.repeat && this.useRepeatKeyboardProtocol()) {
      return KeyboardEventType.repeat
    }

    return KeyboardEventType.keydown
  }

  private getKeyboardState(event: KeyboardEvent): number {
    let state = 0

    // Bit 0-7  NumLock  1:On 0:Off
    if (event.getModifierState("NumLock")) {
      state |= 1
    }
    // Bit 8-15 CapsLock 1:On 0:Off
    if (event.getModifierState("CapsLock")) {
      state |= 1 << 8
    }
    // Bit 16  ctrl 1:按下 0:抬起
    if (event.ctrlKey) {
      state |= 1 << 16
    }
    // Bit 17  shift 1:按下 0:抬起
    if (event.shiftKey) {
      state |= 1 << 17
    }
    // Bit 18  alt 1:按下 0:抬起
    if (event.altKey) {
      state |= 1 << 18
    }
    // Bit 19  win 1:按下 0:抬起
    if (event.metaKey) {
      state |= 1 << 19
    }
    // Bit 20  ScrollLock  1:On 0:Off
    if (event.getModifierState("ScrollLock")) {
      state |= 1 << 20
    }

    if (this.useRepeatKeyboardProtocol()) {
      state = (state | (0x80000000 >>> 0)) >>> 0
    }

    return state >>> 0
  }

  private useRepeatKeyboardProtocol(): boolean {
    const keyboardVersion = ConnectionUseCase.getKeyboardVersion()

    if (keyboardVersion === undefined || keyboardVersion === "v1") {
      return false
    }

    if (!ConnectionUseCase.isGameCloudComputer()) {
      return true
    }

    return !this.config.isGameModel
  }
}
